import { useEffect, useMemo, useState } from "react";

import { useApiOne, useMutation } from "../api/hooks";
import { Field, Page, Switch, useToast } from "../components/ui";

type Json = Record<string, unknown>;

/**
 * Public and server settings (02 §15). The documents are typed on the server
 * (defaults fill gaps); here they are edited as a flat form with sections,
 * one PUT per document.
 */
export function SettingsPage() {
  const { data, loading, error, reload } = useApiOne<{ public: Json; server: Json }>("/admin/settings");
  const [tab, setTab] = useState<"public" | "server">("public");
  const [draft, setDraft] = useState<Json | null>(null);
  const [dirty, setDirty] = useState(false);
  const { run, busy } = useMutation();
  const toast = useToast();

  useEffect(() => {
    if (data) {
      setDraft(structuredClone(data[tab]));
      setDirty(false);
    }
  }, [data, tab]);

  const sections = useMemo(() => (draft ? groupSections(draft) : []), [draft]);

  function setPath(path: string[], value: unknown) {
    setDraft((d) => {
      if (!d) return d;
      const next = structuredClone(d);
      let cur: Json = next;
      for (const k of path.slice(0, -1)) cur = cur[k] as Json;
      cur[path[path.length - 1]] = value;
      return next;
    });
    setDirty(true);
  }

  async function save() {
    if (!draft) return;
    const r = await run(`/admin/settings/${tab}`, { method: "PUT", body: draft });
    if (r.ok) {
      toast("Settings saved");
      reload();
      if (tab === "public") location.reload();
    } else toast(r.error, "err");
  }

  if (error) return <p className="error">{error}</p>;

  return (
    <Page
      title="Settings"
      actions={
        <button type="button" className="btn btn-primary" disabled={!dirty || busy} onClick={save}>
          {busy ? "Saving…" : `Save ${tab} settings`}
        </button>
      }
    >
      <div className="tabs">
        <button type="button" className={tab === "public" ? "active" : undefined} onClick={() => setTab("public")}>
          Public (apps &amp; config)
        </button>
        <button type="button" className={tab === "server" ? "active" : undefined} onClick={() => setTab("server")}>
          Server (rules &amp; limits)
        </button>
      </div>
      {!draft && loading && <p>Loading…</p>}
      <div className="grid">
        {sections.map((s) => (
          <section key={s.title} className="card">
            <h2>{titleCase(s.title)}</h2>
            <div className="form">
              {s.entries.map(([key, value, path]) => (
                <Field key={key} label={titleCase(key)}>
                  {typeof value === "boolean" ? (
                    <Switch checked={value} onChange={(v) => setPath(path, v)} />
                  ) : typeof value === "number" ? (
                    <input className="input" type="number" step="any" value={value} onChange={(e) => setPath(path, Number(e.target.value))} />
                  ) : key.startsWith("status_") || (s.title === "colors" && typeof value === "string") ? (
                    <span className="color-field">
                      <input type="color" value={String(value)} onChange={(e) => setPath(path, e.target.value)} />
                      <input className="input mono" value={String(value)} onChange={(e) => setPath(path, e.target.value)} />
                    </span>
                  ) : (
                    <input className="input" value={value === null ? "" : String(value)} placeholder={value === null ? "not set" : undefined} onChange={(e) => setPath(path, e.target.value === "" ? null : e.target.value)} />
                  )}
                </Field>
              ))}
            </div>
          </section>
        ))}
      </div>
    </Page>
  );
}

type Section = { title: string; entries: [string, unknown, string[]][] };

function groupSections(doc: Json): Section[] {
  const general: Section = { title: "general", entries: [] };
  const out: Section[] = [general];
  for (const [k, v] of Object.entries(doc)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out.push({ title: k, entries: Object.entries(v as Json).map(([k2, v2]) => [k2, v2, [k, k2]]) });
    } else {
      general.entries.push([k, v, [k]]);
    }
  }
  return out.filter((s) => s.entries.length > 0);
}

function titleCase(s: string) {
  return s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
